import React, { useState, useEffect, useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { LoadingPanel } from "../LoadingPanel/LoadingPanel";
import { reloadOptions } from "../../request/api";
import { sendLoadingPostRequest } from "../../request/request";
import { TextField, ThemeProvider, createTheme, styled } from "@mui/material";
import FormContext from "../../contexts/FormContext";
import "./StepPPP.css";
import { SurveyMainType } from "../../types/SurveyMainType";
import { GenerateQuestion } from "../Surveys/GenerateQuestion";
import { IStep2FormInput } from "../../types/IStep2FormInput";
import { DictionaryCombo } from "../../types/DictionaryCombo";
import { Dictionary } from "../../types/Dictionary";
import { SurveyListType } from "../../types/SurveyListType";
import { PPPType } from "../../types/PPPType";
import { CheckHighRisk } from "../../types/CheckHighRisk";

const TextFieldStyled = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 50,
  },
}));

const defaultMaterialTheme = createTheme({
  palette: {
    primary: {
      main: "#000000",
    },
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.MuiInputLabel-marginDense": {
            transform: "translate(14px, 20px) scale(1)",
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -6px) scale(1) !important",
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          display: "none",
        },
      },
    },
  },
});

interface IStepPPPFormInput {
  sourceOfAssetValues: string[];
}

interface IStep0Form {
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

const StepPPPHighRisk = ({ setStep }: IStep0Form) => {
  const context = useContext(FormContext);
  const [waitForApi, setWaitForApi] = useState<boolean>(true);
  const [checkOption, setCheckOption] = useState<CheckHighRisk>();
  const [loading, setLoading] = useState<boolean>(false);
  const [pppData, setPppData] = useState<SurveyMainType>();
  const [highRiskPppData, setHighRiskPppData] = useState<SurveyMainType>();
  const [isHighRisk, setIsHighRisk] = useState<boolean>();
  const [intervalHandler, setIntervalHandler] = useState<NodeJS.Timeout>();
  const [errorIds, setErrorIds] = useState<number[]>();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    getValues,
    setValue,
    clearErrors,
    setError,
    trigger,
  } = useForm<IStep2FormInput>({
    defaultValues: context.step2,
    mode: "onChange",
    shouldFocusError: false,
  });

  const scrollToElement = (name: string) => {
    let firstErrorElement: HTMLElement | null =
      document.getElementsByName(name)[0];
    if (!firstErrorElement) {
      firstErrorElement = document.getElementById(name);
    }
    if (firstErrorElement) {
      firstErrorElement.scrollIntoView({ behavior: `smooth`, block: "center" });
    }
  };

  const isEmpty = (text?: string) => {
    if (text == null || text === "" || text.length === 0) return true;
    return false;
  };

  const getPPP = async () => {
    reloadOptions();
    const { data: response, success } = await sendLoadingPostRequest<
      never,
      SurveyListType
    >({
      loadingFunction: setLoading,
      endpoint: "agreement/GetPPP",
    });
    if (success && response?.data) {
      const resp = response?.data;
      if (resp == null) return;
      setPppData(resp.survey);
      setHighRiskPppData(resp.surveyHighRisk);
      //setIsHighRisk(resp.isHighRisk);
    }
  };

  const setHightRisk = async () => {
    if (intervalHandler) clearInterval(intervalHandler);
    setWaitForApi(false);
    if (context?.ppp?.surveyHighRisk == null) {
      await getPPP();
    } else {
      setHighRiskPppData(context.ppp.surveyHighRisk);
      setIsHighRisk(true);
    }
  };

  const setWithoutHighRisk = async () => {
    const { data: response, success } = await sendLoadingPostRequest<
      never,
      SurveyListType
    >({
      loadingFunction: setLoading,
      endpoint: "agreement/GetPPP",
    });
    if (success && response?.data) {
      const resp = response?.data;
      if (resp == null) return;
      if (context.setPPP)
        context.setPPP((prev) =>
          prev
            ? {
                ...prev,
                isHighRisk: false,
                surveyHighRisk: resp?.surveyHighRisk,
              }
            : undefined,
        );
    }

    if (intervalHandler) clearInterval(intervalHandler);
    setStep(4);
  };

  useEffect(() => {
    setErrorIds([]);
  }, [highRiskPppData]);
  const checkHighRisk = async () => {
    if (intervalHandler) clearInterval(intervalHandler);

    if (context.ppp?.withBeneficiary == true) {
      setHightRisk();
      return;
    }

    reloadOptions();
    setLoading(true);
    const localData = {
      survey: pppData,
      surveyHighRisk: highRiskPppData,
      isHighRisk: isHighRisk,
      withBeneficiary: context.ppp?.withBeneficiary,
      firstBeneficiary: context.ppp?.firstBeneficiary,
      secondBeneficiary: context.ppp?.secondBeneficiary,
      thirdBeneficiary: context.ppp?.thirdBeneficiary,
    };

    const { data: response, success } = await sendLoadingPostRequest<
      PPPType,
      CheckHighRisk
    >({
      loadingFunction: setLoading,
      endpoint: "agreement/CheckHighRisk",
      params: {
        surveyList: localData,
        profession: getValues("profession"),
      },
    });
    if (success && response?.data) {
      setCheckOption(response?.data);
      if (response?.data.forceHighRisk == true) {
        await setWithoutHighRisk();
        console.log("force");
      } else {
        if (response.data.check == null) {
          const inter = setTimeout(
            checkHighRisk,
            response.data.requestTime * 1000,
          );
          setIntervalHandler(inter);
        } else if (response.data.check == true) {
          await setHightRisk();
        } else {
          await setWithoutHighRisk();
          console.log("else");
        }
      }
    }
    setLoading(false);
  };

  const getError = (data: SurveyMainType, checkTouched: boolean) => {
    const localErrorIds: number[] = [];
    data?.questions.forEach((question) => {
      if (
        question.isRequired &&
        question.type !== 1 &&
        (checkTouched || question.isTouched)
      ) {
        if (
          question.answers?.filter(
            (ans) =>
              (ans.isSelected && !ans.showTextBox) ||
              (ans.isSelected &&
                ans.showTextBox &&
                !isEmpty(ans.additionalText)),
          ).length !== question.answers?.filter((ans) => ans.isSelected).length
        ) {
          localErrorIds.push(question.id);
        }
      }
    });
    return localErrorIds;
  };

  const checkErrors = (checkTouched: boolean) => {
    const localErrorIds: number[] = [];

    if (isHighRisk && highRiskPppData != null) {
      localErrorIds.push.apply(
        localErrorIds,
        getError(highRiskPppData, checkTouched),
      );
    }

    setErrorIds(localErrorIds.length > 0 ? localErrorIds : []);
    return localErrorIds;
  };

  const handleOnClickNext = async () => {
    const check = checkErrors(true);
    console.log(highRiskPppData, "highRiskPppData");

    if (check.length === 0) {
      const localData = {
        survey: context.ppp?.survey,
        surveyHighRisk: highRiskPppData,
        isHighRisk: isHighRisk,
        withBeneficiary: context.ppp?.withBeneficiary,
        firstBeneficiary: context.ppp?.firstBeneficiary,
        secondBeneficiary: context.ppp?.secondBeneficiary,
        thirdBeneficiary: context.ppp?.thirdBeneficiary,
      };

      if (context.setPPP) {
        context.setPPP(localData);
      }
      if (context.setStep2 && context.step2) {
        context.setStep2({
          ...context.step2,
          profession: getValues("profession"),
        });
      }
      if (intervalHandler) clearInterval(intervalHandler);
      setStep(4);
    } else {
      if (check && check.length > 0) {
        const firstId = check[0];
        scrollToElement(`Question-${firstId}`);
      }
    }
  };

  useEffect(() => {
    if (context.ppp?.isHighRisk) {
      setHightRisk();
    } else {
      checkHighRisk();
    }
    console.log(context.ppp?.checkHighRisk, "context.ppp?.checkHighRisk");
    if (context.ppp?.checkHighRisk) setCheckOption(context.ppp?.checkHighRisk);

    window.scrollTo(0, 0);

    return () => {
      if (intervalHandler) clearInterval(intervalHandler);
    };
  }, []);

  useEffect(() => {
    checkErrors(false);
  }, [pppData]);

  const changeToMinutes = (seconds: number) => {
    let minutes = Math.floor(seconds / 60);
    const remindSeconds = seconds % 60;
    if (remindSeconds != 0) minutes++;

    return minutes;
  };

  if (waitForApi)
    return (
      <>
        <p>Trwa ocena ryzyka klienta, prosimy o chwilę cierpliwości.</p>
        {checkOption ? (
          <p>
            Proces może zająć do {changeToMinutes(checkOption.maxTime)} minut
          </p>
        ) : null}
        <button
          onClick={() => {
            if (context.setPPP) {
              context.setPPP((prev) =>
                prev
                  ? {
                      ...prev,
                      isHighRisk: isHighRisk,
                    }
                  : {
                      isHighRisk: isHighRisk,
                    },
              );
            }
            if (intervalHandler) clearInterval(intervalHandler);
            setStep(7);
          }}
          className="backBtn"
          type="submit"
        >
          Cofnij
        </button>
      </>
    );

  return (
    <ThemeProvider theme={defaultMaterialTheme}>
      <hr />

      <p className="bold red section">
        Formularz danych dotyczący przeciwdziałaniu praniu pieniędzy
      </p>

      {highRiskPppData?.questions?.map((question) => (
        <GenerateQuestion
          key={question.id}
          question={question}
          pppData={setHighRiskPppData}
          errorIds={errorIds}
        />
      ))}

      <div className="row px-3 mb-3 mt-4">
        <div className="col-sm-12 code">
          <div className="mr-12 float-right">
            <button
              onClick={() => {
                if (context.setPPP) {
                  context.setPPP((prev) =>
                    prev
                      ? {
                          ...prev,
                          surveyHighRisk: highRiskPppData,
                          isHighRisk: isHighRisk,
                        }
                      : {
                          surveyHighRisk: highRiskPppData,
                          isHighRisk: isHighRisk,
                        },
                  );
                }
                setStep(7);
              }}
              className="backBtn"
              type="submit"
            >
              Cofnij
            </button>
            <input
              onClick={handleOnClickNext}
              className="nextBtn"
              type="submit"
              value="Dalej"
            />
          </div>
        </div>
      </div>

      {loading && <LoadingPanel />}
    </ThemeProvider>
  );
};

export { StepPPPHighRisk };
